import type { CSSProperties, ReactNode } from 'react'
import type { TimeOfDay } from '@/shared/timeOfDay'
import { SipayMark } from './Brand'
import { cn } from './primitives'

/**
 * The sky behind the greeting, by the hour in Jakarta.
 *
 * Decoration with a job: it is the one element that changes between the
 * first login of the day and the last, so the page never looks frozen. Login
 * and Beranda draw the same one, so signing in and landing feel like one
 * product. The colours are literal rather than tokens because they are a
 * picture, not UI -- nothing else in the app is ever drawn in them.
 */
const SKY: Record<TimeOfDay, { top: string; mid: string; low: string; sun: string; left: string; y: string; size: number }> = {
  pagi: { top: '#0a3f93', mid: '#0a8fe6', low: '#8fd8ff', sun: '#ffd452', left: '58%', y: '62%', size: 150 },
  siang: { top: '#0057b8', mid: '#00a3f5', low: '#c8eeff', sun: '#fff6cf', left: '66%', y: '-14%', size: 150 },
  sore: { top: '#231a64', mid: '#6b36d9', low: '#ff8a5c', sun: '#ffb45e', left: '80%', y: '70%', size: 150 },
  malam: { top: '#050a1c', mid: '#0c1a48', low: '#22397e', sun: '#eef1ff', left: '72%', y: '8%', size: 90 },
}

const STARS = [
  [12, 22, 1.4], [28, 64, 1], [41, 18, 1.2], [55, 48, 1], [63, 30, 1.6],
  [84, 56, 1], [92, 16, 1.3], [7, 78, 1], [35, 38, 1], [76, 82, 1.2],
]
  .map(([x, y, r]) => `radial-gradient(${r}px ${r}px at ${x}% ${y}%, #fff 50%, transparent 51%)`)
  .join(',')

export function Sky({
  tod,
  grid = false,
  markClassName = 'size-[26rem] -right-16 -bottom-32',
  className,
  children,
  'aria-label': ariaLabel,
}: {
  tod: TimeOfDay
  /** A faint grid fading from the top-left corner. Beranda has it; login keeps the sky plain. */
  grid?: boolean
  /** Size and position of the oversized SIPAY mark bleeding off a corner. */
  markClassName?: string
  className?: string
  children: ReactNode
  'aria-label'?: string
}) {
  const sky = SKY[tod]
  const background: CSSProperties = {
    background: `radial-gradient(120% 90% at 85% 110%, ${sky.low}b3, transparent 60%), linear-gradient(160deg, ${sky.top} 0%, ${sky.mid} 70%, ${sky.low} 130%)`,
  }
  const sun: CSSProperties = {
    left: sky.left,
    top: sky.y,
    width: sky.size,
    height: sky.size,
    background: `radial-gradient(circle at 40% 40%, #fff 0%, ${sky.sun} 45%, transparent 72%)`,
    boxShadow: tod === 'malam' ? '0 0 80px 20px rgba(200,210,255,.25)' : `0 0 120px 40px ${sky.sun}73`,
  }

  return (
    <section aria-label={ariaLabel} className={cn('relative isolate overflow-hidden text-white', className)} style={background}>
      {tod === 'malam' && (
        <div aria-hidden="true" className="absolute inset-0 -z-10 opacity-80" style={{ backgroundImage: STARS }} />
      )}
      <div aria-hidden="true" className="absolute -z-10 rounded-full transition-all duration-1000" style={sun} />
      {grid && (
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 opacity-15"
          style={{
            backgroundImage:
              'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            maskImage: 'radial-gradient(ellipse at 20% 0%, #000 10%, transparent 65%)',
            WebkitMaskImage: 'radial-gradient(ellipse at 20% 0%, #000 10%, transparent 65%)',
          }}
        />
      )}
      <SipayMark className={cn('pointer-events-none absolute -z-10 opacity-10 brightness-0 invert', markClassName)} />
      {children}
    </section>
  )
}
